import React, { useState, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import Modal from "react-modal"; // Ensure Modal is imported from the chosen library

function MapSearch({ initialLatitude, initialLongitude, onLocationChange }) {
  const [latitude, setLatitude] = useState(initialLatitude || "");
  const [longitude, setLongitude] = useState(initialLongitude || "");
  const [marker, setMarker] = useState(null);
  const [address, setAddress] = useState("");
  const [showAddress, setShowAddress] = useState(null);
  const [error, setError] = useState("");
  const [center, setCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // Default: India center
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: null, // Replace with your API key
  });

  // Sync child state with updated props from the parent
  useEffect(() => {
    setLatitude(initialLatitude);
  }, [initialLatitude]);

  useEffect(() => {
    setLongitude(initialLongitude);
  }, [initialLongitude]);

  useEffect(() => {
    if (navigator.geolocation && !latitude && !longitude) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCenter({ lat: latitude, lng: longitude });
          setMarker({ lat: latitude, lng: longitude });
          setLatitude(latitude);
          setLongitude(longitude);
          // setAddress("Your location");
        },
        (error) => setError(`Error: ${error.message}`)
      );
    } else {
      setCenter({ lat: Number(latitude), lng: Number(longitude) });
      setMarker({ lat: Number(latitude), lng: Number(longitude) });
      setLatitude(Number(latitude));
      setLongitude(Number(longitude));
    }
  }, [latitude, longitude]);

  const handleMapClick = (event) => {
    const { latLng } = event;
    const lat = latLng.lat();
    const lng = latLng.lng();

    setMarker({ lat, lng });
    setLatitude(lat);
    setLongitude(lng);
    onLocationChange({ lat, lng }); // Trigger the callback with new location
  };

  const searchLocation = async () => {
    if (address) {
      try {
        const encodedAddress = encodeURIComponent(address);
        const url = `https://google-map-places.p.rapidapi.com/maps/api/geocode/json?address=${encodedAddress}&language=en&region=en&result_type=administrative_area_level_1&location_type=APPROXIMATE`;
        const options = {
          method: "GET",
          headers: {
            "x-rapidapi-key":
              "f5a7147a2bmshd8653d5ac7e2be5p15e0aejsn88747900de20",
            "x-rapidapi-host": "google-map-places.p.rapidapi.com",
          },
        };

        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error("Failed to fetch location");
        }
        const data = await response.json();

        // console.log("data", data.results[0]);

        if (data.results.length > 0) {
          setShowAddress(address);
          const location = data.results[0].geometry.location;
          setCenter({ lat: location.lat, lng: location.lng });
          setMarker({ lat: location.lat, lng: location.lng });
          setLatitude(location.lat);
          setLongitude(location.lng);
          onLocationChange({ lat: location.lat, lng: location.lng }); // Notify parent of the change
          setError("");
        } else {
          setShowAddress(null);
          setError("Location not found!");
        }
      } catch (error) {
        setError(
          "An error occurred while fetching the location. Please try again."
        );
      }
    } else {
      setError("Please enter an address.");
    }
  };

  if (loadError) return <div>Error loading maps: {loadError.message}</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <>
      <button
        onClick={() => setModalIsOpen(true)}
        className="btn bg-blue-500 text-white mb-2 rounded-sm !h-9 px-2 py-1 ml-2"
      >
        Set Coordinates
      </button>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel="Map Search Modal"
        style={{
          content: {
            width: "80%", // Adjust the width as needed
            height: "70%", // Adjust the height as needed
            margin: "auto",
            padding: "20px",
          },
        }}
      >
        <div className="float-right p-3 py-4">
          <button
            onClick={() => setModalIsOpen(false)}
            className="absolute top-4 right-4 p-2 text-white bg-red-500 rounded-sm"
            style={{
              border: "none",
              cursor: "pointer",
            }}
          >
            X
          </button>
        </div>
        <div className="container mx-auto mt-8 grid gap-12 grid-cols-2">
          <form className="space-y-4">
            <input
              type="text"
              id="address"
              placeholder="Enter an address"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                setShowAddress("");
              }}
              className="form-control custom-height !border-2 border-blueGray-300 rounded-sm"
            />
            <button
              type="button"
              className="py-2 px-4 text-white bg-blue-500 rounded-md custom-height"
              onClick={searchLocation}
            >
              Search
            </button>
            {error && <p className="text-red-500">{error}</p>}
            <input
              type="text"
              id="latitude"
              placeholder="Latitude"
              value={latitude}
              readOnly
              className="form-control custom-height !border-2 border-blueGray-300 rounded-sm"
            />
            <input
              type="text"
              id="longitude"
              placeholder="Longitude"
              value={longitude}
              readOnly
              className="form-control custom-height !border-2 border-blueGray-300 rounded-sm"
            />
          </form>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "400px" }}
            center={center}
            zoom={15}
            options={{
              disableDefaultUI: false,
              scrollwheel: true,
              gestureHandling: "greedy",
            }}
            onClick={handleMapClick}
          >
            {marker && (
              <>
                <Marker
                  position={marker}
                  label={
                    showAddress
                      ? {
                          text: address,
                          color: "#000000",
                          fontSize: "16px",
                          fontWeight: "bold",
                        }
                      : undefined
                  }
                />
              </>
            )}
          </GoogleMap>
        </div>
      </Modal>
    </>
  );
}

export default MapSearch;
